import PopupAlert from "@pathwright/ui/src/components/alert/PopupAlert"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useCallback, useRef, useState } from "react"
import { downloadReport } from "./utils"

// A failed browser request must not leave a global loading toast on screen forever.
const REPORT_DOWNLOAD_LOADING_TIMEOUT = 60_000

const getReportUrl = async (reportUrl) => {
  const resolvedReportUrl =
    typeof reportUrl === "function" ? await reportUrl() : reportUrl

  if (resolvedReportUrl?.error) {
    throw new Error(resolvedReportUrl.error)
  }

  if (typeof resolvedReportUrl !== "string") {
    throw new Error("No report URL available.")
  }

  return resolvedReportUrl
}

// simple HOC for handling PopupAlert for status when
// downloading a report
const DownloadReport = ({ children, emptyMessage }) => {
  const { t } = useTranslate()
  const downloadRequestIdRef = useRef(0)

  const initialState = {
    loading: null,
    success: null,
    info: null,
    error: null
  }

  const [state, setState] = useState(initialState)
  const clearState = useCallback(() => setState(initialState), [])

  const handleDownloadReport = async (reportUrl) => {
    const downloadRequestId = downloadRequestIdRef.current + 1
    downloadRequestIdRef.current = downloadRequestId

    try {
      setState({
        loading: {
          title: `${t("Downloading report")}...`,
          description: t(
            "Feel free to close this message and continue working while your download is processing."
          )
        },
        error: null
      })
      const result = await downloadReport(await getReportUrl(reportUrl))
      // Ignore stale async download requests
      if (downloadRequestIdRef.current !== downloadRequestId) return

      if (result?.status === "empty") {
        setState({
          ...initialState,
          info: emptyMessage || t("No results found for this report.")
        })
        return
      }

      setState({
        ...initialState,
        success: t("Finished downloading report.")
      })
    } catch (error) {
      // Ignore stale async download requests
      if (downloadRequestIdRef.current !== downloadRequestId) return
      setState({
        ...initialState,
        error: error.message ? error.message : error
      })
    }
  }

  return (
    <>
      <PopupAlert
        loading={state.loading}
        success={state.success}
        info={state.info}
        error={state.error}
        onClear={clearState}
        clearAfter={state.loading ? REPORT_DOWNLOAD_LOADING_TIMEOUT : 3000}
        dismissOnUnmount
      />
      {children({
        handleDownloadReport
      })}
    </>
  )
}

DownloadReport.displayName = "DownloadReport"

export default DownloadReport
